/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react';
import type { AxiosError } from 'axios';
import type { ActionType } from '../Students';
import type { OptionType } from '../../../../../util/types';
import { Translate, useI18n } from '@whizz/react-i18n';
import { Button, ButtonIcon, ChoiceGroup, DateRangePicker, FieldGroup, Input, Loader, Selector } from '../../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../../global';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../../util/vars';
import type {
  RosterManagementStudentListItem,
  Tag as TagType,
  TagsGet,
  YearGroupInterface,
} from '@whizz/api-model';
import { AssessmentStartAgeDelta } from '@whizz/api-model';
import { repository } from '@whizz/api-client';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { FormikHelpers } from 'formik';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import { API_ERRORS } from '../../../../../util/constants';
import { ActiveToggle, TagCheckbox } from './AddEditStudentModal.components';
import { DateTime } from 'luxon';
import { YearGroupSelector } from '../../../modules';
import { ModalFooter } from '../../../../../components/modal/Modal';
import { useCleverIntegration } from '../../../../../hooks/useCleverIntegration';
import { getDateStringWithLocale } from '../../../../../util/dates';
import useYearGroups from '../../../../../hooks/useYearGroups';

export interface ModalProps extends ToggleModalProps {
  student?: RosterManagementStudentListItem;
  onNotEnoughSubscriptions: () => void,
  onStudentDuplicate: () => void,
  onSuccess?: (actionType?: ActionType) => void;
  onError?: () => void;
}

const MIN_LABEL_WIDTH = 150;

type AssessmentDeltasType = keyof typeof AssessmentStartAgeDelta;

const INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  gender: null,
  dob: '',
  upn: '',
  studentU: '',
  studentP: '',
  classesIds: [ null ] as unknown as number[],
  yearGroupId: null as number | null,
  tagsIds: [] as number[],
  aboveBelowAssessmentDelta: 'below',
  assessmentDelta: AssessmentStartAgeDelta.MONTHS_BELOW_6,
  active: true,
};

const AddEditStudentModal = ({ student, toggle, onNotEnoughSubscriptions, onStudentDuplicate, onSuccess, onError }: ModalProps): JSX.Element => {
  const { locale, translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const queryClient = useQueryClient();
  const [ yearGroupsQuery ] = useYearGroups();

  const editMode = null != student;

  const [ showPassword, setShowPassword ] = useState(false);
  const [ showDatePicker, setShowDatePicker ] = useState(false);

  // Classes
  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES, 'withoutArchived' ],
    () => repository.NewClassRepository.getClasses({ includeArchived: false }),
    {
      placeholderData: [],
    },
  );

  const classesData = classesQuery.data!;
  const classesOptions = classesData.map((c) => ({
    value: c.id,
    label: c.name,
    yearGroupId: c.yearGroupId,
  }));

  // Tags
  const tagsQuery = useQuery<TagsGet>(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
    () => repository.TagsRepository.getTags(),
    {
      enabled: true,
      placeholderData: [],
    },
  );

  const tagsData = tagsQuery.data!;
  const defaultTags = tagsData.filter((tag: TagType) => tag.isDefault);
  const schoolTags = tagsData.filter((tag: TagType) => !tag.isDefault);
  const fetchAssessmentDelta = !!student?.id;

  const aboveBelowStartAssessmentOptions = [
    {
      label: <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.ABOVE_BELOW_ASSESSMENT_DELTAS.ABOVE'} values={{}}>
        {[ <strong /> ]}
      </Translate>,
      value: 'above',
    },
    {
      label: <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.ABOVE_BELOW_ASSESSMENT_DELTAS.BELOW'} values={{}}>
        {[ <strong /> ]}
      </Translate>,
      value: 'below',
    },
  ];

  const assessmentDelta = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.ASSESSMENT_DELTA, student?.id ],
    async () => repository.StudentRepository.assessmentDelta(student!.id),
    {
      enabled: fetchAssessmentDelta,
    },
  );
  const setAssessmentDelta = useMutation(
    ({ childId, delta }: { childId: number, delta: AssessmentStartAgeDelta }) => {
      return repository.StudentRepository.setAssessmentDelta(childId, delta);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.ASSESSMENT_DELTA ]);
      },
    },
  );

  const activateMutation = useMutation(
    ({ childId }: { childId: number, askResetAssessment?: boolean, studentCreated?: boolean }) => {
      return repository.StudentRepository.activate([ childId ]);
    },
    {
      onSuccess: (data, variables ) => {
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
        onSuccess?.(variables.studentCreated ? { type: 'created', studentId: variables.childId } : variables.askResetAssessment ? { type: 'delta', studentId: variables.childId } : undefined);
        toggle();
      },
      onError: (error: AxiosError<{ error: string }>, variables) => {
        // Do not throw an error when creating the student
        if (variables.studentCreated) {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
          onSuccess?.({ type: 'created', studentId: variables.childId });
          toggle();
          return;
        }
        if (null == error.response) {
          return;
        }
        switch (error.response.data.error) {
          case API_ERRORS.NOT_ENOUGH_SUBSCRIPTIONS:
            onNotEnoughSubscriptions();
            break;
          default:
            onError?.();
            break;
        }

        toggle(false);
      },
    },
  );

  const deactivateMutation = useMutation(
    ({ childId }: { childId: number }) => {
      return repository.StudentRepository.deactivate([ childId ]);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
        onSuccess?.();
        toggle();
      },
    },
  );

  const createMutation = useMutation(
    ({ formData } : { formData: FormData, formikHelpers: FormikHelpers<FormData> }): Promise<{ childId: number }> => {
      return repository.StudentRepository.create(formData);
    },
    {
      onSuccess: (response, { formData }) => {

        const setDelta = studentData?.assessmentDelta !== formData.assessmentDelta;
        if (setDelta) {
          setAssessmentDelta.mutate({ childId: response.childId, delta: Number(formData.assessmentDelta) });
        }

        if (formData.active) {
          activateMutation.mutate({ childId: response.childId, studentCreated: true });

          return;
        }

        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
        onSuccess?.({ type: 'created', studentId: response.childId });
        toggle();
      },
      onError: (error: AxiosError<{ error: string }>, { formikHelpers }) => {
        if (null == error.response) {
          return;
        }

        switch (error.response.data.error) {
          case API_ERRORS.USERNAME_ALREADY_EXISTS:
            formikHelpers.setFieldError('username', translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.USERNAME.ALREADY_EXISTS'));
            break;
          case API_ERRORS.UPN_ALREADY_EXISTS:
            formikHelpers.setFieldError('upn', translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.UPN.ALREADY_EXISTS'));
            break;
          case API_ERRORS.DUPLICATE_STUDENT:
            onStudentDuplicate();
            break;
          default:
            onError?.();
            break;
        }
      },
    },
  );

  const updateMutation = useMutation(
    ({ formData } : { formData: FormData, formikHelpers: FormikHelpers<FormData> }): Promise<void> => {
      return repository.StudentRepository.update(student!.id, formData);
    },
    {
      onSuccess: (response, { formData }) => {

        const setDelta = studentData?.assessmentDelta !== formData.assessmentDelta;
        if (setDelta) {
          setAssessmentDelta.mutate({ childId: student!.id, delta: Number(formData.assessmentDelta) });
        }

        if (student?.active && !formData.active) {
          deactivateMutation.mutate({ childId: student!.id });

          return;
        }

        if (!student?.active && formData.active) {
          activateMutation.mutate({ childId: student!.id, askResetAssessment: setDelta });

          return;
        }

        const askResetAssessment = student?.active && setDelta;
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
        onSuccess?.(askResetAssessment ? { type: 'delta', studentId: student!.id } : undefined);
        toggle();
      },
      onError: (error: AxiosError<{ error: string }>, { formikHelpers }) => {
        if (null == error.response) {
          return;
        }

        switch (error.response.data.error) {
          case API_ERRORS.USERNAME_ALREADY_EXISTS:
            formikHelpers.setFieldError('username', translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.USERNAME.ALREADY_EXISTS'));
            break;
          case API_ERRORS.UPN_ALREADY_EXISTS:
            formikHelpers.setFieldError('upn', translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.UPN.ALREADY_EXISTS'));
            break;
          case API_ERRORS.DUPLICATE_STUDENT:
            onStudentDuplicate();
            break;
          default:
            onError?.();
            break;
        }
      },
    },
  );

  const handleSubmit = (formData: FormData, formikHelpers: FormikHelpers<FormData>) => {

    if (null == student) {
      const castedFormData = VALIDATION_SCHEMA_ADD.cast(formData);

      createMutation.mutate({
        formData:{
          ...castedFormData,
          username: castedFormData.studentU,
          password: castedFormData.studentP,
          dob: DateTime.fromJSDate(castedFormData.dob!).toISODate(),
          assessmentDelta: formData.assessmentDelta,
        } as FormData,
        formikHelpers,
      });
    } else {
      const castedFormData = VALIDATION_SCHEMA_EDIT.cast(formData);

      updateMutation.mutate({
        formData:{
          ...castedFormData,
          username: castedFormData.studentU,
          password: castedFormData.studentP,
          dob: DateTime.fromJSDate(castedFormData.dob!).toISODate(),
          assessmentDelta: formData.assessmentDelta,
        } as FormData,
        formikHelpers,
      });
    }
  };

  // If options have not loaded yet,
  // show Loader component
  const isLoading = (!classesQuery.isFetched || classesQuery.isFetching)
    || (!yearGroupsQuery.isFetched || yearGroupsQuery.isFetching)
    || (!classesQuery.isFetched || classesQuery.isFetching)
    || (fetchAssessmentDelta && (!assessmentDelta.isFetched || assessmentDelta.isFetching));

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || activateMutation.isLoading || deactivateMutation.isLoading;

  if (isLoading) {
    return (<Loader />);
  }

  const studentData: FormData | null = null != student
    ? {
      ...student,
      studentU: student.username,
      studentP: student.password,
      classesIds: student.classes.map((c) => c.id),
      yearGroupId: student.yearGroupId ?? null,
      tagsIds: student.tags.map((t) => t.id),
      assessmentDelta: assessmentDelta.data ?? AssessmentStartAgeDelta.MONTHS_BELOW_6,
      aboveBelowAssessmentDelta: assessmentDelta.data && assessmentDelta.data > 0 ? 'above' : 'below',
    }
    : null
  ;

  const VALIDATION_SCHEMA_ADD = Yup.object()
    .shape({
      firstName: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.FIRST_NAME.REQUIRED')),
      lastName: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.LAST_NAME.REQUIRED')),
      gender: Yup.number().nullable().required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.GENDER.REQUIRED')),
      dob: Yup.date()
        .required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.DOB.REQUIRED'))
        .max(DateTime.now().toJSDate(), translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.DOB.MAX'))
      ,
      upn: Yup.string()
        .required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.UPN.REQUIRED'))
        .transform((value) => '' !== value ? value : null),
      studentU: Yup.string()
        .nullable()
        .transform((value) => '' !== value ? value : null),
      studentP: Yup.string()
        .nullable()
        .transform((value) => '' !== value ? value : null)
        .min(5, translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.PASSWORD.MIN', { min: 5 }))
        .max(20, translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.PASSWORD.MAX'))
      ,
      classesIds: Yup.array()
        .of(Yup.number().nullable())
        .min(1)
        .test({
          name: 'firstRequired',
          message: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.CLASS.REQUIRED'),
          test: (classes) => null != classes![0],
        })
      ,
      yearGroupId: Yup.number()
        .typeError(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.YEAR_GROUP.REQUIRED'))
        .required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.YEAR_GROUP.REQUIRED'))
      ,
      tagsIds: Yup.array()
        .of(Yup.number()),
      active: Yup.boolean(),
    });

  const VALIDATION_SCHEMA_EDIT = VALIDATION_SCHEMA_ADD
    .shape({
      ...(!studentData?.upn && { upn: Yup.string().nullable().transform((value) => '' !== value ? value : null) }),
      studentU: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.USERNAME.REQUIRED')),
      studentP: Yup.string()
        .required(translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.PASSWORD.REQUIRED'))
        .min(5, translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.PASSWORD.MIN', { min: 5 }))
        .max(20, translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.VALIDATIONS.PASSWORD.MAX')),
    });

  return (
    <Formik <FormData>
      initialValues={Object.assign({ ...INITIAL_VALUES },  studentData)}
      initialErrors={{}}
      validationSchema={!editMode ? VALIDATION_SCHEMA_ADD : VALIDATION_SCHEMA_EDIT}
      onSubmit={(formData, formikHelpers) => handleSubmit(formData as FormData, formikHelpers)}
    >
      {({ handleSubmit, values, setFieldValue, getFieldProps, errors, isValid, touched, resetForm }) => {
        const hasSelectedClass = values.classesIds.some((c) => null != c);
        const assessmentDeltas = Object.keys(AssessmentStartAgeDelta).filter((value) => isNaN(parseFloat(value))).filter((value) => values.aboveBelowAssessmentDelta === 'below' ? AssessmentStartAgeDelta[value as AssessmentDeltasType] < 0 : AssessmentStartAgeDelta[value as AssessmentDeltasType] > 0).map((delta) => ({
          label: translate(`pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.ASSESSMENT_DELTAS.${delta}`),
          value: AssessmentStartAgeDelta[delta as AssessmentDeltasType],
        }));
        return (
          <form onSubmit={handleSubmit} autoComplete={'off'}>
            <div className={'container space-y-6'}>
              <Input
                {...getFieldProps('firstName')}
                id={'first_name'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.FIRST_NAME')}
                labelStyle={'bold'}
                minWidthForLabel={MIN_LABEL_WIDTH}
                error={touched.firstName ? errors.firstName : null}
                required
                disabled={isClever}
                autoComplete={'off'}
              />
              <Input
                {...getFieldProps('lastName')}
                id={'last_name'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.LAST_NAME')}
                labelStyle={'bold'}
                minWidthForLabel={MIN_LABEL_WIDTH}
                error={touched.lastName ? errors.lastName : null}
                required
                disabled={isClever}
                autoComplete={'off'}
              />
              <FieldGroup
                id={'gender'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.GENDER')}
                labelStyle={'bold'}
                error={touched.gender ? errors.gender : null}
                required
              >
                <div className={'flex space-x-4'}>
                  <ChoiceGroup
                    id={'female'}
                    name={'gender'}
                    type={'radio'}
                    label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.GENDERS.female')}
                    value={2}
                    checked={2 === values.gender}
                    onChange={() => setFieldValue('gender', 2)}
                    disabled={isClever}
                  />
                  <ChoiceGroup
                    id={'male'}
                    name={'gender'}
                    type={'radio'}
                    label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.GENDERS.male')}
                    value={1}
                    checked={1 === values.gender}
                    onChange={() => setFieldValue('gender', 1)}
                    disabled={isClever}
                  />
                  <ChoiceGroup
                    id={'other'}
                    name={'gender'}
                    type={'radio'}
                    label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.GENDERS.other')}
                    value={3}
                    checked={3 === values.gender}
                    onChange={() => setFieldValue('gender', 3)}
                    disabled={isClever}
                  />
                </div>
              </FieldGroup>
              <div>
                <Input
                  {...getFieldProps('dob')}
                  value={values.dob ? getDateStringWithLocale(DateTime.fromFormat(values.dob, 'yyyy-MM-dd').toJSDate(), locale?.getId()) : ''  }
                  id={'dob'}
                  label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.DOB')}
                  labelStyle={'bold'}
                  minWidthForLabel={MIN_LABEL_WIDTH}
                  error={!showDatePicker && touched.dob ? errors.dob : null}
                  required
                  disabled={isClever}
                  icon={GLOBAL_ICONS.calendar}
                  readOnly
                  onClick={() => setShowDatePicker((previous) => !previous)}
                />
                {showDatePicker ?
                  <div className={'mt-4'}>
                    <DateRangePicker
                      hideInputs
                      startDate={null}
                      endDate={null}
                      showMonthDropdown
                      showYearDropdown
                      selected={values.dob ? DateTime.fromFormat(values.dob, 'yyyy-MM-dd').toJSDate() : null}
                      maxDate={DateTime.now().toJSDate()}
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('dob', DateTime.fromJSDate(e).toISODate());
                        }
                        setShowDatePicker(false);
                      }}
                      yearItemNumber={16}
                    />
                  </div> : null }
              </div>
              <Input
                {...getFieldProps('upn')}
                id={'upn'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.UPN')}
                labelStyle={'bold'}
                hint={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.HINTS.UPN')}
                minWidthForLabel={MIN_LABEL_WIDTH}
                error={touched.upn ? errors.upn : null}
                required={!editMode || !!studentData?.upn}
                disabled={isClever}
                autoComplete={'off'}
              />
              <Input
                {...getFieldProps('studentU')}
                id={'studentU'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.USERNAME')}
                labelStyle={'bold'}
                hint={!editMode ? translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.HINTS.USERNAME') : null}
                minWidthForLabel={MIN_LABEL_WIDTH}
                error={touched.studentU ? errors.studentU : null}
                autoComplete={'off'}
              />
              <Input
                {...getFieldProps('studentP')}
                id={'studentP'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.PASSWORD')}
                labelStyle={'bold'}
                hint={!editMode ? translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.HINTS.PASSWORD') : null}
                type={showPassword ? 'text' : 'password'}
                minWidthForLabel={MIN_LABEL_WIDTH}
                icon={GLOBAL_ICONS[showPassword ? 'see_off' : 'see_on']}
                onIconClick={() => setShowPassword(!showPassword)}
                error={touched.studentP ? errors.studentP : null}
                autoComplete={'off'}
              />
              <FieldArray name={'classesIds'}>
                {({ push, replace, remove }) => (
                  <div className={'space-y-2'}>
                    {values.classesIds.map((item, index) => {
                      const removePreviousClassesFromOptions = classesOptions.filter((option) => {
                        const notAlreadySelectedOrCurrentSelectedOption = !values.classesIds.includes(option.value) || option.value === item;
                        return notAlreadySelectedOrCurrentSelectedOption;
                      });
                      return (
                        <React.Fragment key={index}>
                          <div className={'flex items-end flex-nowrap'}>
                            <div className={'w-full'}>
                              <Selector
                                {...getFieldProps(`classes.${index}`)}
                                key={index}
                                id={'classes'}
                                label={0 === index && translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.CLASSES')}
                                labelStyle={'bold'}
                                options={removePreviousClassesFromOptions}
                                value={removePreviousClassesFromOptions.find((option) => option.value === item)}
                                onChange={(selectedClass: { label: string, value: number, yearGroupId: number }) => {
                                  replace(index, selectedClass?.value);

                                  if (null == values.yearGroupId && 0 === index && null != selectedClass.yearGroupId) {
                                    setFieldValue('yearGroupId', selectedClass.yearGroupId);
                                  }
                                }}
                                error={(null != errors.classesIds && touched.classesIds && 0 === index) ? errors.classesIds : null}
                                required={0 === index}
                              />
                            </div>
                            {0 !== index && (
                              <ButtonIcon
                                icon={GLOBAL_ICONS.delete}
                                onClick={() => remove(index)}
                                isCircle
                                color={'transparent'}
                                title={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.BUTTONS.REMOVE_CLASS')}
                                aria-label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.BUTTONS.REMOVE_CLASS')}
                              />
                            )}
                          </div>
                        </React.Fragment>
                      );})}
                    <Button
                      icon={GLOBAL_ICONS.add}
                      onClick={() => push(null)}
                      asLink
                    >
                      {translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.BUTTONS.ADD_CLASS')}
                    </Button>
                  </div>
                )}
              </FieldArray>
              <YearGroupSelector
                onChange={(selectedOption) => setFieldValue('yearGroupId', selectedOption.value)}
                value={values.yearGroupId}
                error={touched.yearGroupId ? errors.yearGroupId : null}
                options={yearGroupsQuery.data!.map((item: YearGroupInterface) => ({
                  label: item.name,
                  value: item.yearGroupId,
                }))}
                isDisabled={!hasSelectedClass || isClever}
                labelStyle={'bold'}
                required
                disableNoYearGroup
              />
              <FieldGroup
                id={'tags'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.TAGS')}
                labelStyle={'bold'}
              >
                <h6 className={'mb-2'}>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.DEFAULT_TAGS')}</h6>
                <div className={'grid grid-cols-3 md:grid-cols-5 gap-2'}>
                  {defaultTags.map((tag: TagType) => (
                    <TagCheckbox
                      key={tag.abbreviation.toLowerCase()}
                      id={tag.abbreviation.toLowerCase()}
                      label={translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tag.abbreviation}`, { 'defaultValue': tag.abbreviation })}
                      $color={tag.color}
                      checked={values.tagsIds.includes(tag.id)}
                      onChange={() => {
                        if (!values.tagsIds.includes(tag.id)) {
                          setFieldValue('tagsIds', [ ...values.tagsIds, tag.id ]);

                          return;
                        }

                        setFieldValue('tagsIds', [ ...values.tagsIds.filter((tagId) => tagId != tag.id) ]);
                      }}
                    />
                  ))}
                </div>
                {schoolTags.length ? (
                  <>
                    <h6 className={'mt-4 mb-2'}>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.CUSTOM_TAGS')}</h6>
                    <div className={'grid grid-cols-3 md:grid-cols-5 gap-2'}>
                      {schoolTags.map((tag: TagType) => (
                        <TagCheckbox
                          key={tag.abbreviation.toLowerCase()}
                          id={tag.abbreviation.toLowerCase()}
                          label={tag.abbreviation}
                          $color={tag.color}
                          checked={values.tagsIds.includes(tag.id)}
                          onChange={() => {
                            if (!values.tagsIds.includes(tag.id)) {
                              setFieldValue('tagsIds', [ ...values.tagsIds, tag.id ]);
                              return;
                            }

                            setFieldValue('tagsIds', [ ...values.tagsIds.filter((tagId) => tagId != tag.id) ]);
                          }}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </FieldGroup>
              <FieldGroup
                id={'active'}
                label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.LICENSE')}
                labelStyle={'bold'}
                error={touched.active ? errors.active : null}
                required
              >
                <ActiveToggle
                  checked={values.active}
                  onChange={(checked) => setFieldValue('active', checked)}
                />
              </FieldGroup>
              <div className={'w-full'}>
                <h6 className={'font-bold'}>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.ASSESSMENT_DELTA')}</h6>
                <div className={'w-full flex space-x-4'}>
                  <div className='w-1/3'>
                    <Selector
                      id={'above_below_assessment_delta'}
                      options={aboveBelowStartAssessmentOptions}
                      value={aboveBelowStartAssessmentOptions.find((option) => option.value === values.aboveBelowAssessmentDelta)}
                      onChange={(selectedOption: OptionType) => {
                        setFieldValue('aboveBelowAssessmentDelta', selectedOption.value);
                        setFieldValue('assessmentDelta', selectedOption.value === 'below' ? AssessmentStartAgeDelta.MONTHS_BELOW_6 : AssessmentStartAgeDelta.YEARS_ABOVE_1);
                      }}
                      isClearable={false}
                      isDisabled={!values.active}
                    />
                  </div>
                  <div className='w-2/3'>
                    <Selector
                      id={'assessment_delta'}
                      options={assessmentDeltas}
                      value={assessmentDeltas.find((option) => option.value === values.assessmentDelta)}
                      onChange={(selectedOption: OptionType) => {
                        setFieldValue('assessmentDelta', selectedOption.value);
                      }}
                      error={touched.assessmentDelta ? errors.assessmentDelta : null}
                      isClearable={false}
                      isDisabled={!values.active}
                    />
                  </div>
                </div>
              </div>
              {(!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData]))
              && (
                <FormGenericError />
              )}
            </div>
            <ModalFooter justify={editMode ? 'between' : 'end'}>
              {editMode
                ? (
                  <CancelButtonModal
                    align={'start'}
                    onClick={() => resetForm()}
                  >
                    {translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.BUTTONS.DISCARD')}
                  </CancelButtonModal>
                )
                : null}

              <SaveButton
                type={'submit'}
                isLoading={isSubmitting}
              />
            </ModalFooter>
          </form>
        );
      }}
    </Formik>
  );
};

export default AddEditStudentModal;

export interface FormData {
  firstName: string;
  lastName: string;
  gender: number | null;
  dob: string | null;
  upn: string | null;
  studentU: string | null;
  studentP: string | null
  classesIds: number[];
  yearGroupId: number | null;
  tagsIds: number[];
  active: boolean;
  assessmentDelta: number;
  aboveBelowAssessmentDelta?: 'below' | 'above';
}

